// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import { Box, FormControl, Grid, Select, TextField, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Button, ConfirmationButton, FileUploadModal, PDFPreview, StandardDialog } from '@roc/ui';
import { VerifiedChip } from 'libs/ui/src/lib/components/chip/verifiedChip';
import { TrackRecordTable } from './trackRecordTable';
import { TRACK_RECORD_CAPITAL_PROVIDER, TRACK_RECORD_REVIEW_TYPE, formatDate } from '@roc/feature-utils';
import { useState } from 'react';
import { useBorrowerTrackRecordStore } from '@roc/feature-borrowers';
import { usdAmount } from 'libs/ui/src/lib/components/borrowerBaseballCard/utils';
import { TrackRecordPropertyExpandedPanel } from './trackRecordPropertyExpandedPanel';

interface TrackRecordPropertiesProps {
  store: any;
  borrowerBaseballCardStore: any;
  isInternal?: boolean;
}

export const TrackRecordProperties = observer(({
  store,
  borrowerBaseballCardStore,
  isInternal = false,
}: TrackRecordPropertiesProps) => {
  const { borrowerTrackRecordStore } = useBorrowerTrackRecordStore();
  // The backend requires both ids; in the loan-task flow loanId only comes from the task document.
  const getVerificationRequestIds = () => {
    const borrowerId = store.borrowerInfo?.borrowerId || borrowerBaseballCardStore?.currentBorrower;
    const loanId = borrowerBaseballCardStore?.loanId || borrowerTrackRecordStore?.document?.loanId;
    if (!borrowerId || !loanId) {
      store.globalStore?.notificationStore?.showErrorNotification({ message: 'Unable to request verification: missing borrower or loan information.' });
      return null;
    }
    return { borrowerId, loanId };
  };
  const [uploadDocument, setUploadDocument] = useState(false);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [currentRowType, setCurrentRowType] = useState(null);

  // Verify / unverify property (internal only) — mirrors newBorrowerTrackRecordCard.
  const [currentProperty, setCurrentProperty] = useState<any>(null);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [isUnverifyOpen, setIsUnverifyOpen] = useState(false);
  const [verificationSource, setVerificationSource] = useState<string>('');
  const [othersReason, setOthersReason] = useState('');
  const [unverifyReason, setUnverifyReason] = useState('');
  // Multi-select bulk actions (mirrors newBorrowerTrackRecordCard).
  const [selectedProperties, setSelectedProperties] = useState<any[]>([]);
  const [isMultiVerify, setIsMultiVerify] = useState(false);

  const lockedData = borrowerBaseballCardStore?.baseballCardDetails?.lockedData;

  const selectedIds = selectedProperties.map((p) => p.verifiedTrackRecordsId);

  const toggleRow = (row) => {
    const obj = row.object;
    setSelectedProperties((prev) =>
      prev.some((p) => p.verifiedTrackRecordsId === obj.verifiedTrackRecordsId)
        ? prev.filter((p) => p.verifiedTrackRecordsId !== obj.verifiedTrackRecordsId)
        : [...prev, obj]
    );
  };

  const toggleAllRows = (tableRows, checked) => {
    const objs = tableRows.map((r) => r.object);
    setSelectedProperties((prev) => {
      if (checked) {
        const existing = new Set(prev.map((p) => p.verifiedTrackRecordsId));
        return [...prev, ...objs.filter((o) => !existing.has(o.verifiedTrackRecordsId))];
      }
      const toRemove = new Set(objs.map((o) => o.verifiedTrackRecordsId));
      return prev.filter((p) => !toRemove.has(p.verifiedTrackRecordsId));
    });
  };

  const clearSelection = () => setSelectedProperties([]);

  const trackRecordPropertiesHeaders = [
    { name: 'Property Address', width: '18%' },
    { name: 'Purchase Date', width: '9%' },
    { name: 'Purchase Amount', width: '10%' },
    { name: 'Rehab Budget', width: '9%' },
    { name: 'Seller Name', width: '10%' },
    { name: 'Sale Date', width: '9%' },
    { name: 'Sale Price', width: '9%' },
    { name: 'Verification Status', width: '8%' },
    { name: 'Entity Name', width: '12%' },
    { name: 'Buyer Name', width: '10%' },
  ];

  const openUploadDocument = async (id, type) => {
    setCurrentRowId(id);
    setCurrentRowType(type);
    setUploadDocument(true);
  };

  const handleUploadDocument = async (id, files, type) => {
    const taskPropertyBody = {
      sectionProcess: 'TRACK_RECORD_REVIEW',
      objectType: 'PROPERTY_TRACK_RECORD',
      taskTemplateType: 'PROPERTY_TRACK_RECORD',
      objectId: id,
    };

    store.createTaskWithFile(taskPropertyBody, files[0]);
  };

  const previewDocument = (id, name, fileExtension) => {
    store.downloadTaskFile(id, name, fileExtension);
  };

  const refreshProperties = () => {
    store.fetchTrackRecordProperties();
    // The Borrower Experience "Verified" counters read borrowerBaseballCardStore.trackRercords,
    borrowerBaseballCardStore?.getTrackRecords(
      borrowerBaseballCardStore?.currentBorrower,
      borrowerBaseballCardStore?.loanId,
    );
  };

  const openVerify = (property) => {
    setIsMultiVerify(false);
    setCurrentProperty(property);
    setVerificationSource('');
    setOthersReason('');
    setIsVerifyOpen(true);
  };

  const openMultiVerify = () => {
    setIsMultiVerify(true);
    setVerificationSource('');
    setOthersReason('');
    setIsVerifyOpen(true);
  };

  const closeVerify = () => {
    setIsVerifyOpen(false);
    setIsMultiVerify(false);
    setCurrentProperty(null);
    setVerificationSource('');
    setOthersReason('');
  };

  const doVerify = async () => {
    if (isMultiVerify) {
      const body = selectedProperties.map((p) => ({
        ...p,
        purchaseDate: formatDate(p.purchaseDate, 'MM/dd/yyyy'),
        saleDate: formatDate(p.saleDate, 'MM/dd/yyyy'),
      }));
      await borrowerBaseballCardStore?.verifyMultipleTrackRecords(body, borrowerBaseballCardStore?.loanId, verificationSource, othersReason);
      clearSelection();
    } else {
      const body = {
        ...currentProperty,
        purchaseDate: formatDate(currentProperty.purchaseDate, 'MM/dd/yyyy'),
        saleDate: formatDate(currentProperty.saleDate, 'MM/dd/yyyy'),
      };
      await borrowerBaseballCardStore?.verifyTrackRecord(body, borrowerBaseballCardStore?.loanId, verificationSource, othersReason);
    }
    closeVerify();
    refreshProperties();
  };

  const openUnverify = (property) => {
    setCurrentProperty(property);
    setUnverifyReason('');
    setIsUnverifyOpen(true);
  };

  const closeUnverify = () => {
    setIsUnverifyOpen(false);
    setCurrentProperty(null);
    setUnverifyReason('');
  };

  const doUnverify = async () => {
    await borrowerBaseballCardStore?.unverifyTrackRecord(currentProperty.verifiedTrackRecordsId, borrowerBaseballCardStore?.loanId, unverifyReason);
    closeUnverify();
    refreshProperties();
  };

  const verifyDisabled = !verificationSource || (verificationSource === 'Other' && !othersReason);

  // Create Resolution (internal) — mirrors newBorrowerTrackRecordCard's
  // handleRequestVerification: flags the property, then requests verification
  // for every property currently marked isVerificationRequested.
  const getResolutionItem = (property) => {
    const fullAddress =
      (property.purchaseAddress ? property.purchaseAddress + ', ' : '') +
      (property.purchaseCity ? property.purchaseCity + ', ' : '') +
      (property.purchaseState ? property.purchaseState + ' ' : '') +
      (property.purchaseZipcode ? property.purchaseZipcode : '');
    const itemDescription =
      `Address: ${fullAddress}\n` +
      (property.entityName ? 'Entity name: ' + property.entityName : '') +
      (property.buyerName ? ' Buyer name: ' + property.buyerName : '');
    return {
      itemSection: TRACK_RECORD_REVIEW_TYPE + ' ' + property.verifiedTrackRecordsId,
      itemName: fullAddress.trim(),
      itemDescription,
    };
  };

  const handleCreateResolution = async (property) => {
    const ids = getVerificationRequestIds();
    if (!ids) return;
    const { borrowerId, loanId } = ids;
    runInAction(() => {
      property.purchaseDate = formatDate(property.purchaseDate, 'MM/dd/yyyy');
      property.saleDate = formatDate(property.saleDate, 'MM/dd/yyyy');
      property.isVerificationRequested = true;
    });
    if (property.verifiedTrackRecordsId == null) {
      const response = await borrowerBaseballCardStore?.savePropertyInformation(property) as any;
      runInAction(() => { property.verifiedTrackRecordsId = response?.data?.verifiedTrackRecordsId; });
    }
    const trackRecords = [];
    const resolutions = [];
    for (const tr of (store.properties || [])) {
      if (tr.isVerificationRequested === true) {
        trackRecords.push(tr);
        resolutions.push(getResolutionItem(tr));
      }
    }
    const request = { borrowerId, letterOfExplanation: resolutions, trackRecords };
    await borrowerBaseballCardStore?.requestTrackRecordVerification(request, borrowerId, loanId, TRACK_RECORD_REVIEW_TYPE);
    refreshProperties();
  };

  // ── Bulk actions over the selected rows (mirror newBorrowerTrackRecordCard) ──
  const handleRemoveMultiple = async () => {
    const ids = selectedProperties.map((p) => p.verifiedTrackRecordsId);
    await borrowerBaseballCardStore?.deleteMultipleTrackRecords(ids);
    clearSelection();
    refreshProperties();
  };

  const handleRequestMultiple = async () => {
    const ids = getVerificationRequestIds();
    if (!ids) return;
    const { borrowerId, loanId } = ids;
    for (const property of selectedProperties) {
      runInAction(() => {
        property.purchaseDate = formatDate(property.purchaseDate, 'MM/dd/yyyy');
        property.saleDate = formatDate(property.saleDate, 'MM/dd/yyyy');
        property.isVerificationRequested = true;
      });
      if (property.verifiedTrackRecordsId == null) {
        const response = await borrowerBaseballCardStore?.savePropertyInformation(property) as any;
        runInAction(() => { property.verifiedTrackRecordsId = response?.data?.verifiedTrackRecordsId; });
      }
    }
    const trackRecords = [];
    const resolutions = [];
    for (const tr of (store.properties || [])) {
      if (tr.isVerificationRequested === true) {
        trackRecords.push(tr);
        resolutions.push(getResolutionItem(tr));
      }
    }
    const request = { borrowerId, letterOfExplanation: resolutions, trackRecords };
    await borrowerBaseballCardStore?.requestTrackRecordVerification(request, borrowerId, loanId, TRACK_RECORD_REVIEW_TYPE);
    clearSelection();
    refreshProperties();
  };

  const bulkRemoveDisabled = selectedProperties.some((p) => p.isVerified || p.isVerificationRequested);

  // Some property addresses come back with the city/state/zip repeated
  const formatPropertyAddress = (raw: any) => {
    const seenSegments = new Set<string>();
    return String(raw || '')
      .split(',')
      .map((part) => part.trim())
      .filter((part) => {
        if (!part) return false;
        const key = part.toLowerCase();
        if (seenSegments.has(key)) return false;
        seenSegments.add(key);
        return true;
      })
      .join(', ');
  };

  const trackRecordPropertiesRows = store.properties?.reduce((rows, property, currentIndex) => {
    const propertyAddress = formatPropertyAddress(
      property.fullAddress
        ? property.fullAddress
        : `${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}`,
    );
    const notCapitalProvider = property.recordSourceType && property.recordSourceType !== TRACK_RECORD_CAPITAL_PROVIDER;

    rows.push({
      id: property.verifiedTrackRecordsId,
      object: property,
      type: 'property',
      cells: [
        <Typography
          style={{
            color: store.doesPropertyHasParsingError(property.purchaseAddress) ? '#d32f2f' : '#222222',
          }}
        >
          {propertyAddress}
        </Typography>,
        property.purchaseDate ? property.purchaseDate : '-',
        property.purchasePrice ? usdAmount({ value: property.purchasePrice }) : '$0.00',
        property.rehabBudget ? usdAmount({ value: property.rehabBudget }) : '$0.00',
        property.purchasedFrom ? property.purchasedFrom : '-',
        property.saleDate ? property.saleDate : '-',
        property.salePrice ? usdAmount({ value: property.salePrice }) : '$0.00',
        <Grid container justifyContent="center">
          <VerifiedChip isVerified={property.isVerified} />
        </Grid>,
        property.entityName ? property.entityName : '-',
        property.soldTo ? property.soldTo : '-',
      ],
      expandableContent: (
        <TrackRecordPropertyExpandedPanel
          property={property}
          store={store}
          borrowerBaseballCardStore={borrowerBaseballCardStore}
          isInternal={isInternal}
          previewDocument={previewDocument}
        />
      ),
      onUploadDocument: () => openUploadDocument(property.verifiedTrackRecordsId, 'property'),
      // Action gating mirrors prod (newBorrowerTrackRecordCard). Note Capital
      // Provider records expose only Upload (Edit/Unverify are excluded).
      canEdit:
        (isInternal && property.isVerified && notCapitalProvider)
        || (!property.isVerified && !property.verificationInProgress),
      onEdit: () => store.handleEditExistingTrackRecordProperty(property, currentIndex),
      canVerify: isInternal && !property.isVerified && !property.verificationInProgress,
      onVerify: () => openVerify(property),
      canUnverify: isInternal && property.isVerified && notCapitalProvider && !lockedData,
      onUnverify: () => openUnverify(property),
      canCreateResolution: isInternal && !property.isVerified && !property.verificationInProgress && !property.isVerificationRequested,
      onCreateResolution: () => handleCreateResolution(property),
      canRemove: !property.isVerified && !property.verificationInProgress && !property.isVerificationRequested,
    });
    return rows;
  }, []);

  return (
    <Grid container direction='row' spacing={2}>
      {selectedProperties.length > 0 && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="body2" style={{ marginRight: 'auto' }}>
              {selectedProperties.length} selected
            </Typography>
            <ConfirmationButton
              variant="outlined"
              color="primary"
              disabled={bulkRemoveDisabled}
              onClick={handleRemoveMultiple}
              testId="bulkRemoveProperties"
              confirmDialogProps={{
                title: 'Remove Properties',
                body: 'Are you sure you want to remove this properties ?',
              }}
            >
              Remove
            </ConfirmationButton>
            {isInternal && (
              <ConfirmationButton
                variant="outlined"
                color="primary"
                onClick={handleRequestMultiple}
                testId="bulkRequestVerification"
                confirmDialogProps={{
                  title: 'Request Verification',
                  body: 'Are you sure you want to request verification for this properties ?',
                }}
              >
                Request Verification
              </ConfirmationButton>
            )}
            {isInternal && (
              <Button variant="outlined" color="primary" testId="bulkVerifyProperties" onClick={openMultiVerify}>
                Verify Properties
              </Button>
            )}
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <TrackRecordTable
          isExpandable={true}
          store={store}
          headers={trackRecordPropertiesHeaders}
          rows={[...(trackRecordPropertiesRows ?? [])].reverse()}
          enablePagination={(store.properties?.length ?? 0) > 5}
          borrowerBaseballCardStore={borrowerBaseballCardStore}
          selectable={true}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAllRows={toggleAllRows}
        />
      </Grid>
      <FileUploadModal
        open={uploadDocument}
        title={`Upload Document`}
        onClose={() => setUploadDocument(false)}
        onUpload={(files) => handleUploadDocument(currentRowId, files, currentRowType)}
      />
      <PDFPreview
        open={store.isPdfPreviewModalOpen}
        title={store.pdfTitle}
        data={store.pdfData}
        headers={{}}
        fileName={store.pdfTitle}
        onClose={() => {
          store.pdfTitle = null;
          store.pdfData = null;
          store.isPdfPreviewModalOpen = false;
        }}
      />

      <StandardDialog
        open={isVerifyOpen}
        maxWidth="sm"
        title={'Verify Property'}
        handleClose={closeVerify}
        dialogContent={
          <Grid container direction="column">
            <Grid>
              <Typography variant="body1" style={{ marginBottom: '25px' }}>
                What is the verification source ?
                <FormControl style={{ marginLeft: '10px', position: 'relative', bottom: '3px', marginRight: '5px' }}>
                  <Select value={verificationSource} native onChange={(e) => setVerificationSource(e.target.value as string)}>
                    <option aria-label="None" value="" />
                    <option value="RPR">RPR</option>
                    <option value="Roc Title History">Roc Title History</option>
                    <option value="HUD">HUD</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>
              </Typography>
              {verificationSource === 'Other' && (
                <>
                  <Typography>Please specify:</Typography>
                  <FormControl style={{ width: '100%', marginBottom: '16px', paddingRight: '64px' }}>
                    <TextField
                      variant="outlined"
                      id="propertyVerificationOthersReason"
                      value={othersReason}
                      onChange={(e) => setOthersReason(e.target.value)}
                    />
                  </FormControl>
                </>
              )}
              <Typography variant="body1">
                Are you sure you want to mark this property as verified ?
              </Typography>
            </Grid>
          </Grid>
        }
        dialogActions={
          <Grid container direction="row" justifyContent="flex-end">
            <Button testId="cancelVerifyProperty" style={{ marginRight: '10px' }} onClick={closeVerify}>
              Cancel
            </Button>
            <Button testId="confirmVerifyProperty" disabled={verifyDisabled} color="primary" variant="outlined" onClick={doVerify}>
              Verify
            </Button>
          </Grid>
        }
      />

      <StandardDialog
        open={isUnverifyOpen}
        maxWidth="sm"
        title="Unverify Property"
        handleClose={closeUnverify}
        dialogContent={
          <Grid container direction="column">
            <Grid>
              <Typography>Please specify the reason:</Typography>
              <FormControl style={{ width: '100%', marginBottom: '16px', paddingRight: '64px' }}>
                <TextField
                  variant="outlined"
                  id="propertyUnverificationReason"
                  value={unverifyReason}
                  onChange={(e) => setUnverifyReason(e.target.value)}
                />
              </FormControl>
              <Typography variant="body1">
                Are you sure you want to mark this property as unverified ?
              </Typography>
            </Grid>
          </Grid>
        }
        dialogActions={
          <Grid container direction="row" justifyContent="flex-end">
            <Button testId="cancelUnverifyProperty" style={{ marginRight: '10px' }} onClick={closeUnverify}>
              Cancel
            </Button>
            <Button testId="confirmUnverifyProperty" disabled={!unverifyReason} color="primary" variant="outlined" onClick={doUnverify}>
              Unverify
            </Button>
          </Grid>
        }
      />
    </Grid>
  );
});
